import { useEffect } from 'react';
import { useAuthStore } from '../features/authentication/store/authStore';
import { useContextStore } from '../stores/contextStore';
import { useSubjectStore } from '../stores/subjectStore';
import { useCurrentRoleContext } from '../features/role-context';

/**
 * Context Manager Hook
 * Automatically manages active context based on user authentication and role context
 * Now uses the role context system for precise context management
 */
export const useContextManager = () => {
  const { userSession, isAuthenticated } = useAuthStore();
  const { setActiveContext, clearActiveContext } = useContextStore();
  const setActiveSubjectId = useSubjectStore((state) => state.setActiveSubjectId);

  // Get current role context from client-side storage
  const { data: currentRoleContext, error: roleContextError } = useCurrentRoleContext();

  useEffect(() => {
    if (!isAuthenticated) {
      // User is not authenticated, clear all context
      clearActiveContext();
      return;
    }

    if (!userSession) {
      // No user session available, clear context
      clearActiveContext();
      return;
    }

    if (roleContextError) {
      // Fallback to basic tenant context
      const tenantId = userSession.currentTenantId;
      if (tenantId) {
        setActiveContext({ tenantId });
      } else {
        clearActiveContext();
      }
      return;
    }

    if (currentRoleContext) {
      // Set complete context hierarchy from role context
      setActiveContext({
        tenantId: currentRoleContext.tenantId,
        domainId: currentRoleContext.domainId,
        subjectId: currentRoleContext.subjectId,
      });

      // Update subject store for backward compatibility
      if (currentRoleContext.subjectId) {
        setActiveSubjectId(currentRoleContext.subjectId);
      }
    } else {
      // No role context available, use basic tenant context
      const tenantId = userSession.currentTenantId;
      if (tenantId) {
        setActiveContext({ tenantId });
      } else {
        clearActiveContext();
      }
    }
  }, [
    isAuthenticated,
    userSession,
    currentRoleContext,
    roleContextError,
    setActiveContext,
    clearActiveContext,
    setActiveSubjectId,
  ]);
};
